// src/components/extensions/editor.styles.ts
// The editor chrome shared by every inline extension demo.
//
// Extracted when the second demo arrived. The fold demo needed a frame, a file
// tab, a scrolling code area and a gutter; the regex demo needs the same four,
// and copying them would have meant two places to change the moment the code
// font, the line height or the tab padding needed adjusting. What is left in
// each demo's own styles file is only what is genuinely specific to it.
//
// Shared, deliberately:
//   monoStack         the code font, so every sample is set identically
//   EditorFrame       the inset panel the code sits in
//   TabBar/FileKind   the file tab, which is what makes this read as "a file"
//   CodeArea          the horizontally scrollable region
//   CodeLine          gutter + number + text, on one grid so text aligns
//   LineNumber        the number column
//   GutterSpacer      the empty gutter cell, keeping un-guttered lines aligned
//   CodeText          the code itself
//
// Not shared, and therefore not here: each demo's own control, region and
// overlay. Those are the parts that differ, and folding is not explaining.
import styled, { css } from 'styled-components';

/** The code font. Also used by the demos for their own mono labels. */
export const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

/**
 * Reduced-motion guard, repeated rather than inherited for the same reason
 * Navbar.styles.ts keeps its own: these class rules outrank the global block in
 * globalStyles.ts.
 */
export const noMotion = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * The panel. `surface` rather than `background` so the code sits a shade away
 * from the card behind it and reads as inset, which is what makes it look like a
 * separate surface rather than more card.
 */
export const EditorFrame = styled.div`
  width: 100%;
  min-width: 0;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

/* ------------------------------------------------------------- tab bar */

/**
 * File tab. Fakes an editor tab rather than a toolbar: these extensions act on
 * "the current file", so naming the file is context the demo needs.
 */
export const TabBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  font-family: ${monoStack};
  font-size: 0.7rem;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.colors.secondary};

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.accent};
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/** The part of the filename after the dot, in accent, like a file-type hint. */
export const FileKind = styled.span`
  color: ${({ theme }) => theme.accent};
  opacity: 0.75;
`;

/* ------------------------------------------------------------ code area */

/**
 * The scrolling region. The samples have to keep their line breaks, so `pre`; and
 * a phone is narrower than the longest line, so the overflow is scrollable rather
 * than clipped. Scrollable rather than wrapped because wrapped code with hanging
 * line numbers reads as prose.
 *
 * One consequence worth knowing: a scroll container clips on both axes, so no
 * demo may hang a floating overlay off a line inside here. Overlays are laid out
 * in flow instead.
 */
export const CodeArea = styled.div`
  overflow-x: auto;
  padding: 0.6rem 0;
`;

/**
 * One row: gutter, then the text. `display: grid` rather than padding on the
 * text, so every line's text starts at exactly the same x even as line numbers
 * grow from 9 to 10.
 */
export const CodeLine = styled.div`
  display: grid;
  grid-template-columns: 1.75rem 1.25rem 1fr;
  align-items: center;
  min-width: max-content;
  font-family: ${monoStack};
  font-size: 0.75rem;
  line-height: 1.7;
  white-space: pre;
`;

/** Line numbers. Muted to the point of being background, never read as content. */
export const LineNumber = styled.span`
  padding-right: 0.6rem;
  text-align: right;
  color: ${({ theme }) => theme.borderColor};
  font-variant-numeric: tabular-nums;
  user-select: none;
`;

/** Empty cell where a line has no gutter control, keeping the text aligned. */
export const GutterSpacer = styled.span`
  display: block;
`;

/** The code text itself. */
export const CodeText = styled.span`
  color: ${({ theme }) => theme.text};
`;
